import { ProctoringOptionProfileV2, OptionRelatedInstituteSettings } from "@verity/proctoring-option/types";
import { VerityInstituteSettings } from "../..";
import { useEffect, useId, useRef, useState } from "react";
import { ListItemText, Tooltip, ListItem, FormControlLabel, RadioGroup, List, Typography } from "@mui/material";
import { isInstituteProfile, isEnforcedProfile, isCourseProfile } from "@verity/proctoring-option";
import {
	CoreRadio,
	CoreMenuButton,
	CoreMenuItem,
	CorePagination,
	CoreSearchField,
	CoreChip,
	CoreFocusTrap,
	CoreSearchFieldPaper,
} from "../../core";
import DeleteProfile from "../edit-quiz-modals/deleteProfile";
import ViewProfile from "../edit-quiz-modals/viewProfile";
import i18nPackage from "../../i18n/i18n";
import NoProfileFound from "../images/no-profile-found.svg?react";
import styles from "./profileSection.module.css";
import { ProfileFilterButton, ProfileType } from "./profileFilterButton";
import { useShadowContainer } from "../shadow-dom-context";
import { useMobileLayout } from "../../hooks";

function getProfileType(profile: ProctoringOptionProfileV2): ProfileType {
	if (isInstituteProfile(profile)) {
		return "institute";
	}
	if (isCourseProfile(profile)) {
		return "course";
	}
	return "user";
}

const PAGE_SIZE = 6;
const LIST_PANEL_HEIGHT = PAGE_SIZE * 67;

function ProfileRow({
	profile,
	profileList,
	refreshProfiles,
	selectedProfileId,
	setSelectedProfileId,
	lmsCourseId,
	hasInstituteProfilePermissions,
	optionRelatedInstituteSettings,
	verityInstituteSettings,
	isMobileLayout,
	inShadowDom,
	isFirstUncheckedRadio,
}: {
	profile: ProctoringOptionProfileV2;
	profileList: Array<ProctoringOptionProfileV2>;
	refreshProfiles: () => Promise<void | Map<number, ProctoringOptionProfileV2>>;
	selectedProfileId: number | undefined;
	setSelectedProfileId: (pid: number | undefined) => void;
	lmsCourseId?: string;
	hasInstituteProfilePermissions: boolean;
	optionRelatedInstituteSettings?: OptionRelatedInstituteSettings;
	verityInstituteSettings?: VerityInstituteSettings;
	isMobileLayout: boolean;
	inShadowDom: boolean;
	isFirstUncheckedRadio: boolean;
}) {
	const textRef = useRef<HTMLDivElement>(null);
	const [overflow, setOverflow] = useState(false);
	const [showViewProfile, setShowViewProfile] = useState(false);
	const [menuShowing, setMenuShowing] = useState(false);
	const [deleteModalShowing, setDeleteModalShowing] = useState(false);

	const profileType = getProfileType(profile);
	const badgeColor = profileType === "institute" ? "#D3DDF2" : profileType === "course" ? "#E4E3F6" : "#FAEFDE";

	useEffect(() => {
		if (textRef.current) {
			setOverflow(textRef.current.offsetWidth < textRef.current.scrollWidth);
		}
	}, [profile.name]);

	let name = (
		<ListItemText
			className={`${styles.profileName} ${isMobileLayout ? styles.profileNameCompact : ""}`}
			primaryTypographyProps={{ ref: textRef }}
			primary={profile.name}
		/>
	);
	if (overflow) {
		name = (
			<Tooltip title={profile.name} placement="top">
				{name}
			</Tooltip>
		);
	}

	const onDelete = (pid: number) => {
		if (selectedProfileId === pid) {
			setSelectedProfileId(undefined);
		}
	};

	const isDeleteAllowed = () => {
		return (!isInstituteProfile(profile) || hasInstituteProfilePermissions) && !isEnforcedProfile(profile);
	};

	const radioRef = useRef<HTMLInputElement>(null);

	return (
		<>
			<ListItem role="presentation" className={styles.profileRow} onClick={() => setSelectedProfileId(profile.id)}>
				<FormControlLabel
					control={
						<CoreRadio
							name="selectedProfile"
							id={profile.id.toString()}
							forceTabIndex={false}
							inputRef={radioRef}
							onFocus={() => {
								// If no radio is selected and this is the first radio, make it selected on focus
								if (isFirstUncheckedRadio) setSelectedProfileId(profile.id);
							}}
						/>
					}
					value={profile.id}
					label={name}
					sx={{
						flex: "1 1 0",
					}}
				/>
				<CoreChip
					variant="rectangular"
					chipColor={badgeColor}
					className={styles["profile-badge"]}
					style={{ ...(isMobileLayout && { display: "none" }) }}
				>
					{i18nPackage.t(`editQuizPage.profileSectionTSX.profileType.${profileType}`)}
				</CoreChip>
				<CoreMenuButton
					menuShowing={menuShowing}
					setMenuShowing={setMenuShowing}
					aria-label={i18nPackage.t("editQuizPage.profileSectionTSX.actionsButtonAriaLabel")}
					sx={{ padding: "5px" }}
					onKeyDown={(e: React.KeyboardEvent<HTMLButtonElement>) => {
						// Issue in shadow dom when no radio is selected, shift tabbing from menu button does not work. See comment in ProfileSection
						if (inShadowDom && isFirstUncheckedRadio && e.key === "Tab" && e.shiftKey) {
							e.preventDefault();
							radioRef.current?.focus();
						}
					}}
				>
					<CoreMenuItem aria-haspopup="dialog" onClick={() => setShowViewProfile(true)}>
						<ListItemText primary={i18nPackage.t("editQuizPage.profileSectionTSX.view")} />
					</CoreMenuItem>
					{isDeleteAllowed() && (
						<CoreMenuItem onClick={() => setDeleteModalShowing(true)}>
							<ListItemText primary={i18nPackage.t("editQuizPage.profileSectionTSX.delete")} />
						</CoreMenuItem>
					)}
				</CoreMenuButton>
			</ListItem>

			<ViewProfile
				title={i18nPackage.t("editQuizPage.saveAsProfile.title", { profile: profile.name ?? "-" })}
				open={showViewProfile}
				onClose={() => setShowViewProfile(false)}
				profile={profile}
				viewOnly={true}
				profileTitle={profile.name ?? "-"}
				lmsCourseId={lmsCourseId}
				optionRelatedInstituteSettings={optionRelatedInstituteSettings}
				verityInstituteSettings={verityInstituteSettings}
			/>

			<DeleteProfile
				title={i18nPackage.t("editQuizPage.deleteProfileModal.title", { profile: profile.name ?? "-" })}
				profile={profile}
				refreshProfiles={refreshProfiles}
				profiles={profileList}
				open={deleteModalShowing}
				onDelete={() => onDelete(profile.id)}
				onClose={() => setDeleteModalShowing(false)}
			/>
		</>
	);
}

type ProfileSectionProps = {
	profileList: Array<ProctoringOptionProfileV2>;
	selectedProfileId: number | undefined;
	setSelectedProfileId: (id: number | undefined) => void;
	refreshProfiles: () => Promise<void | Map<number, ProctoringOptionProfileV2>>;
	lmsCourseId?: string;
	optionRelatedInstituteSettings?: OptionRelatedInstituteSettings;
	verityInstituteSettings?: VerityInstituteSettings;
	enforcedProfilesOnly: boolean;
	hasInstituteProfilePermissions: boolean;
	radioGroupAriaId?: string;
	radioGroupAriaLabel?: string;
};

export default function ProfileSection({
	profileList,
	selectedProfileId,
	setSelectedProfileId,
	refreshProfiles,
	lmsCourseId,
	optionRelatedInstituteSettings,
	verityInstituteSettings,
	hasInstituteProfilePermissions,
	enforcedProfilesOnly,
	radioGroupAriaId,
	radioGroupAriaLabel,
}: ProfileSectionProps) {
	const [searchText, setSearchText] = useState("");
	const [filtersApplied, setFiltersAppplied] = useState<ProfileType[]>([]);
	const [profileListPage, setProfileListPage] = useState(0);
	const [radioChecked, setRadioChecked] = useState(false);
	const isMobileLayout = useMobileLayout();
	const shadowContainer = useShadowContainer();
	const searchControlsId = useId();
	const textFieldRef = useRef<HTMLInputElement>(null); // Focus management in shadow dom

	const searchFilter = searchText.toLowerCase();
	const sortFunction = (a: ProctoringOptionProfileV2, b: ProctoringOptionProfileV2) =>
		(a.name ?? "-").localeCompare(b.name ?? "-");

	const displayedProfileList = profileList
		.filter(
			(profile) =>
				(profile.name ?? "-").toLocaleLowerCase().includes(searchFilter) &&
				(enforcedProfilesOnly ? profile.instituteProfile?.enforced : true),
		)
		.filter(
			(profile) =>
				profile.type !== "assessment" &&
				profile.type !== "preset" &&
				(filtersApplied.length === 0 || filtersApplied.includes(profile.type)),
		)
		.sort(sortFunction);

	const totalPages = Math.ceil(displayedProfileList.length / PAGE_SIZE);

	useEffect(() => {
		if (displayedProfileList.length <= profileListPage * PAGE_SIZE) {
			setProfileListPage(Math.max(totalPages - 1, 0));
		}
	}, [displayedProfileList.length, profileListPage, totalPages]);

	const inShadowDom = !!shadowContainer;

	// Manually add tab indexes to radio buttons since radio button layout is different (radio button, then menu button. also pagination)
	// first radio button will be tab focusable if no radio buttons are checked on the current page.
	useEffect(() => {
		const container = inShadowDom ? shadowContainer : document;
		const radios = Array.from(
			container.querySelectorAll<HTMLInputElement>('input[type="radio"][name="selectedProfile"]'),
		);
		const checkedIndex = radios.findIndex((radio) => radio.checked);
		setRadioChecked(checkedIndex >= 0);
		radios.forEach((radio, index) => {
			if (checkedIndex >= 0) {
				// If a radio is checked, only that one gets tabIndex=0
				radio.tabIndex = index === checkedIndex ? 0 : -1;
			} else {
				// No radio checked, so first radio gets tabIndex=0
				radio.tabIndex = index === 0 ? 0 : -1;
			}
		});
	}, [selectedProfileId, displayedProfileList, profileListPage, shadowContainer, inShadowDom]);

	const startingIndex = profileListPage * PAGE_SIZE;
	const profilesToRender = isMobileLayout
		? displayedProfileList
		: displayedProfileList.slice(startingIndex, startingIndex + PAGE_SIZE);

	return (
		<div className={styles["profile-section-container"]}>
			<CoreSearchFieldPaper>
				<ProfileFilterButton filter={filtersApplied} setFilter={setFiltersAppplied} />
				<CoreSearchField
					autoComplete="off"
					label={i18nPackage.t("editQuizPage.profileSectionTSX.placeholder")}
					onSearch={(value) => {
						setProfileListPage(0);
						setSearchText(value);
					}}
					ariaControls={searchControlsId}
					totalResults={displayedProfileList.length}
					inputRef={textFieldRef}
					value={searchText}
				/>
			</CoreSearchFieldPaper>
			<div className={styles["profile-list-container"]}>
				<div
					className={styles.listPanel}
					style={{ height: isMobileLayout ? undefined : LIST_PANEL_HEIGHT }}
					id={searchControlsId}
				>
					{displayedProfileList.length === 0 ? (
						<div className={styles["no-profile-container"]}>
							<NoProfileFound width={isMobileLayout ? "250px" : undefined} />
							<Typography className={styles["no-profile-text"]}>
								{i18nPackage.t("editQuizPage.profileSectionTSX.noProfile")}
							</Typography>
						</div>
					) : (
						<>
							<RadioGroup
								role="presentation"
								value={selectedProfileId ?? -1}
								onChange={(e) => setSelectedProfileId(Number(e.target.value))}
								style={{ maxWidth: isMobileLayout ? "100%" : undefined }}
							>
								<List
									role="radiogroup"
									className={styles.profileList}
									aria-labelledby={radioGroupAriaId}
									aria-label={radioGroupAriaLabel}
								>
									{/** One very specific bug in shadow dom.
									 * Ex: If you choose a profile and click "select", then reopen the modal,
									 * the radio buttons are skipped when tabbing, even though the first radio button has tab index 0.
									 * This only happens in shadow dom, only after you "reopen" the modal, only if no radio button is selected.
									 * So I added this focus trap to manually move focus
									 */}
									{inShadowDom && !radioChecked && (
										<>
											{/**Tabbing into first radio button */}
											<CoreFocusTrap
												onFocus={() => {
													const firstRadio = shadowContainer.querySelector<HTMLInputElement>(
														'input[type="radio"][name="selectedProfile"]',
													);

													firstRadio?.focus();
												}}
											/>
											{/**Shift tabbing from first radio button */}
											<CoreFocusTrap onFocus={() => textFieldRef.current?.focus()} />
										</>
									)}
									{profilesToRender.map((profile, index) => (
										<ProfileRow
											key={profile.id}
											profile={profile}
											profileList={profileList}
											refreshProfiles={refreshProfiles}
											selectedProfileId={selectedProfileId}
											setSelectedProfileId={setSelectedProfileId}
											lmsCourseId={lmsCourseId}
											hasInstituteProfilePermissions={hasInstituteProfilePermissions}
											optionRelatedInstituteSettings={optionRelatedInstituteSettings}
											verityInstituteSettings={verityInstituteSettings}
											inShadowDom={inShadowDom}
											isMobileLayout={isMobileLayout}
											isFirstUncheckedRadio={index === 0 && !radioChecked}
										/>
									))}
								</List>
							</RadioGroup>
							{!isMobileLayout && (
								<div className={styles["profile-pagination-container"]}>
									<CorePagination
										count={totalPages}
										onChange={(_e, page) => setProfileListPage(page - 1)}
										defaultPage={1}
										size={"medium"}
										page={profileListPage + 1}
										paginationWrap={false}
									/>
								</div>
							)}
						</>
					)}
				</div>
			</div>
		</div>
	);
}
